import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { setPassword } from "@/auth/backend";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MIN_LENGTH = 12;

/** The `?token=…` a `/set-password` link carries, or `null` if it has none. */
function useToken(): string | null {
  const raw: unknown = useSearch({ strict: false });
  const token =
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>).token
      : undefined;
  return typeof token === "string" && token !== "" ? token : null;
}

function Frame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-6 px-4">
      <div className="w-full max-w-sm space-y-1 text-center">
        <h1 className="text-xl font-semibold tracking-tight">EstelleERP</h1>
        <p className="text-muted-foreground text-sm">{title}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * `/set-password?token=…` (M10-6, M10-7).
 *
 * One page for two links: an invite mail's "Set your password" and a
 * forgot-password mail's "Choose a new password" both land here, because
 * `POST /api/auth/reset-password` is the one mechanism that finishes either
 * — see `apps/api/src/auth/index.ts`'s doc comment on `createSetPasswordToken`
 * for why. This screen never has to ask which kind of link it was handed;
 * the API tells `invited` from a plain reset apart by whether the account
 * already had a password, and either way the result is the same sentence.
 *
 * Outside the shell, like `/sign-in` — whoever opens this has no session yet.
 */
export function SetPasswordScreen() {
  const navigate = useNavigate();
  const token = useToken();
  const [password, setPasswordValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (token === null) {
    return (
      <Frame title="Set your password">
        <div className="bg-card w-full max-w-sm space-y-2 rounded-xl border p-6 text-center shadow-sm">
          <p className="text-destructive text-sm">
            This link is missing its token.
          </p>
          <p className="text-muted-foreground text-sm">
            Ask whoever invited you to resend it, or use "Forgot password?" on
            the sign-in screen for a fresh one.
          </p>
        </div>
      </Frame>
    );
  }

  if (done) {
    return (
      <Frame title="Password set">
        <div className="bg-card w-full max-w-sm space-y-4 rounded-xl border p-6 text-center shadow-sm">
          <p className="text-sm">
            Your password is set. Sign in with it below.
          </p>
          <Button
            className="w-full"
            onClick={() => {
              void navigate({ to: "/sign-in" });
            }}
          >
            Go to sign in
          </Button>
        </div>
      </Frame>
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    // Unreachable in practice — the `token === null` case above already
    // returned a different screen — but TypeScript does not carry that
    // narrowing into a sibling function's closure, only a check inside this
    // one's own body.
    if (token === null) return;

    if (password.length < MIN_LENGTH) {
      setError(`At least ${String(MIN_LENGTH)} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    void setPassword(token, password).then((result) => {
      setBusy(false);
      if (result.ok) setDone(true);
      else setError(result.message);
    });
  }

  return (
    <Frame title="Choose a password">
      <form
        onSubmit={handleSubmit}
        className="bg-card w-full max-w-sm space-y-4 rounded-xl border p-6 shadow-sm"
      >
        <div className="space-y-1.5">
          <Label htmlFor="set-password-new">New password</Label>
          <Input
            id="set-password-new"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={password}
            onChange={(event) => {
              setPasswordValue(event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="set-password-confirm">Confirm password</Label>
          <Input
            id="set-password-confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_LENGTH}
            value={confirm}
            onChange={(event) => {
              setConfirm(event.target.value);
            }}
          />
        </div>

        {error === null ? null : (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : null}
          Set password
        </Button>
      </form>
    </Frame>
  );
}
